const NON_NAVIGATIONAL_PREFIXES = ['#', 'mailto:', 'tel:', 'javascript:', '@']

const stripTrailingSlash = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path)

/**
 * Normalizes a navigation link into a comparable pathname.
 * @param {string} link - Raw link coming from Contentful (relative or absolute)
 * @param {string|null} host - Current host, used to discard external links
 * @returns {string|null} - Lowercased pathname without query/hash, or null when
 *                          the link can never represent the current page
 */
export const normalizePath = (link, host = null) => {
  if (typeof link !== 'string') return null

  const value = link.trim()
  if (!value || NON_NAVIGATIONAL_PREFIXES.some((prefix) => value.startsWith(prefix))) return null

  let pathname = value

  if (/^(https?:)?\/\//i.test(value)) {
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value)
      // External links never match the current page
      if (!host || url.host !== host) return null
      pathname = url.pathname
    } catch {
      return null
    }
  }

  pathname = pathname.split(/[?#]/)[0]
  if (!pathname.startsWith('/')) pathname = `/${pathname}`

  return stripTrailingSlash(pathname).toLowerCase() || '/'
}

const collectPaths = (items, host, paths = []) => {
  for (const item of items || []) {
    const path = normalizePath(item?.link, host)
    if (path) paths.push(path)
    if (item?.children?.length) collectPaths(item.children, host, paths)
  }
  return paths
}

/**
 * Picks the single navigation link that represents the current page: an exact
 * match wins, otherwise the longest ancestor section (`/a/b` while on `/a/b/c`).
 * Returning one path keeps sibling entries from lighting up at the same time.
 * @param {Array} navigation - Navigation tree
 * @param {string|null} currentUrl - Current browser URL or pathname
 * @returns {string|null} - Normalized path of the active navigation entry
 */
export const findActivePath = (navigation, currentUrl) => {
  if (!currentUrl) return null

  let host = null
  let currentPath = null

  if (/^(https?:)?\/\//i.test(currentUrl)) {
    try {
      const url = new URL(currentUrl.startsWith('//') ? `https:${currentUrl}` : currentUrl)
      host = url.host
      currentPath = normalizePath(url.pathname)
    } catch {
      return null
    }
  } else {
    currentPath = normalizePath(currentUrl)
  }

  if (!currentPath) return null

  const paths = collectPaths(navigation, host)
  if (paths.includes(currentPath)) return currentPath

  return paths
    .filter((path) => path !== '/' && currentPath.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0]
}

/**
 * True when the item itself points to the active path.
 */
export const isItemActive = (item, activePath, host = null) => {
  if (!activePath) return false
  return normalizePath(item?.link, host) === activePath
}

/**
 * True when the item or any of its descendants points to the active path.
 */
export const isBranchActive = (item, activePath, host = null) => {
  if (!activePath || !item) return false
  if (isItemActive(item, activePath, host)) return true
  return (item.children || []).some((child) => isBranchActive(child, activePath, host))
}
